// src/autodesk_usage/components/customerDetail/CustomerLLMSummaryPanel.tsx
// All comments in English only
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";

type UnknownRecord = Record<string, unknown>;

export default function CustomerLLMSummaryPanel({
  summary,
  keyFactors,
  actions,
}: {
  summary?: string | null | unknown;
  keyFactors?: string[] | string | null | unknown;
  actions?: Array<UnknownRecord | string> | UnknownRecord | string | null | unknown;
}) {
  // --- Helpers ---------------------------------------------------------------

  // Parse JSON string safely; return undefined if not JSON.
  const tryParseJSON = (v: unknown): unknown | undefined => {
    if (typeof v !== "string") return undefined;
    const s = v.trim();
    if (!s.startsWith("{") && !s.startsWith("[")) return undefined;
    try {
      return JSON.parse(s);
    } catch {
      return undefined;
    }
  };

  // Normalize to string list
  const toStringList = (v: unknown): string[] => {
    if (Array.isArray(v)) return v.filter(Boolean).map(String);
    if (typeof v === "string") {
      // split by newline/comma/semicolon/bullets
      return v
        .split(/\r?\n|[,;•·]+/g)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };

  // Normalize key factors -> string[]
  const keyFactorsList: string[] = (() => {
    const parsed = tryParseJSON(keyFactors);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    return toStringList(keyFactors);
  })();

  // Normalize actions into two possible shapes:
  // 1) actionsObjects: Array<{action?: string, ...}>
  // 2) actionsPlain: string[]
  let actionsObjects: UnknownRecord[] = [];
  let actionsPlain: string[] = [];

  (() => {
    // Try parse JSON first when actions is string
    const parsed = tryParseJSON(actions);

    // Case A: parsed is array of objects
    if (Array.isArray(parsed) && parsed.every((x) => x && typeof x === "object")) {
      actionsObjects = parsed as UnknownRecord[];
      return;
    }

    // Case B: parsed is a single object
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      actionsObjects = [parsed as UnknownRecord];
      return;
    }

    // Case C: actions already an array of objects
    if (Array.isArray(actions) && actions.every((x) => x && typeof x === "object")) {
      actionsObjects = actions as UnknownRecord[];
      return;
    }

    // Case D: fall back to plain string list
    actionsPlain = toStringList(actions);
  })();

  const summaryText = typeof summary === "string" ? summary : String(summary ?? "");

  const hasContent =
    summaryText.trim().length > 0 ||
    keyFactorsList.length > 0 ||
    actionsObjects.length > 0 ||
    actionsPlain.length > 0;

  if (!hasContent) return null;

  // Render a single action object as a card section
  const renderActionObject = (obj: UnknownRecord, idx: number) => {
    const title = String(obj.action ?? obj.title ?? obj.name ?? `Action #${idx + 1}`);
    const description =
      typeof obj.description === "string" ? obj.description : undefined;

    // Common known fields we want to show in a tidy layout
    const owner = obj.owner;
    const timing = obj.timing_hint ?? obj.timing ?? obj.when;
    const confidence = obj.confidence;
    const playbook = obj.playbook_tag ?? obj.playbook;
    const kpis = obj.kpis;
    const impact = obj.expected_impact ?? obj.impact;

    // Convert a field to readable text
    const toText = (v: unknown): string => {
      if (v == null) return "";
      if (Array.isArray(v)) return v.map(String).join(", ");
      if (typeof v === "object") return JSON.stringify(v);
      return String(v);
    };

    return (
      <div key={idx} className="rounded-lg border p-3 space-y-1 bg-muted/40">
        <div className="font-medium">{title}</div>
        {description && (
          <div className="text-sm text-muted-foreground">{description}</div>
        )}
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {owner ? (
            <>
              <dt className="text-muted-foreground">Owner</dt>
              <dd>{toText(owner)}</dd>
            </>
          ) : null}
          {timing ? (
            <>
              <dt className="text-muted-foreground">Timing</dt>
              <dd>{toText(timing)}</dd>
            </>
          ) : null}
          {confidence != null ? (
            <>
              <dt className="text-muted-foreground">Confidence</dt>
              <dd>{toText(confidence)}</dd>
            </>
          ) : null}
          {playbook ? (
            <>
              <dt className="text-muted-foreground">Playbook</dt>
              <dd>{toText(playbook)}</dd>
            </>
          ) : null}
          {kpis ? (
            <>
              <dt className="text-muted-foreground">KPIs</dt>
              <dd>{toText(kpis)}</dd>
            </>
          ) : null}
          {impact ? (
            <>
              <dt className="text-muted-foreground">Expected Impact</dt>
              <dd>{toText(impact)}</dd>
            </>
          ) : null}
        </dl>
      </div>
    );
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Company LLM Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left side (3/5) */}
          <div className="flex-1 basis-3/5 space-y-6">
            {summaryText && (
              <section className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">
                  Narrative summary
                </div>
                <p className="text-sm leading-6 whitespace-pre-line">
                  {summaryText}
                </p>
              </section>
            )}

            {keyFactorsList.length > 0 && (
              <section className="space-y-2">
                <div className="text-sm font-medium">Key Factors</div>
                <div className="flex flex-wrap gap-2">
                  {keyFactorsList.map((k, i) => (
                    <span
                      key={i}
                      className="text-xs rounded-full border px-2 py-1 bg-muted"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {(actionsObjects.length > 0 || actionsPlain.length > 0) && (
              <section className="space-y-2">
                <div className="text-sm font-medium">Recommended Actions</div>

                {actionsObjects.length > 0 && (
                  <div className="space-y-3">
                    {actionsObjects.map((obj, i) => renderActionObject(obj, i))}
                  </div>
                )}

                {actionsPlain.length > 0 && (
                  <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1 mt-1">
                    {actionsPlain.map((a, i) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </div>

          {/* Right side (2/5) */}
          <aside className="basis-2/5 border-t md:border-t-0 md:border-l border-border pt-4 md:pt-0 md:pl-4 text-sm flex flex-col gap-2 bg-muted/30 rounded-md md:rounded-none md:rounded-r-xl">
            <div className="flex items-center gap-2 font-semibold">
              <Info className="h-4 w-4 text-muted-foreground" />
              How to read this section
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              This AI summary provides a high-level interpretation of the
              customer&apos;s usage pattern, risk evolution, and churn
              probability. Use it to quickly grasp the account&apos;s health
              before diving into the product-level analysis.
            </p>
            <div>
              <div className="font-medium text-sm">Section breakdown</div>
              <ul className="list-disc pl-4 mt-1 text-muted-foreground space-y-1">
                <li>
                  <strong>Narrative summary</strong>: explains the customer&apos;s
                  current risk and usage trend in plain language.
                </li>
                <li>
                  <strong>Key Factors</strong>: show the most influential metrics
                  or signals behind the AI analysis.
                </li>
                <li>
                  <strong>Recommended Actions</strong>: suggest next steps for
                  sales or customer success teams to reduce churn risk or improve
                  renewal chances.
                </li>
              </ul>
            </div>
            <div>
              <div className="font-medium text-sm">Usage tips</div>
              <ul className="list-disc pl-4 mt-1 text-muted-foreground space-y-1">
                <li>Use this summary as a quick pre-meeting briefing.</li>
                <li>
                  Compare with visual trends below to validate AI insights.
                </li>
                <li>
                  Combine with your own CRM context to plan next actions.
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </CardContent>
    </Card>
  );
}
